<template>
  <fts-client-modal
    append-content-class="user-profile-modal__body"
    title="Account"
    class="fts-user-profile-modal"
    :class="{'preferences': section === 'preferences'}"
    ref="ftsClientModal"
  >
    <!-- header:icon -->
    <font-awesome-icon slot="icon" :icon="['far', 'user']" />
    <!-- body -->
    <template #body>
      <div class="user-profile-tabs">
        <div
          class="user-profile-tab"
          :class="{ active: section === x.name }"
          v-for="x in getSections"
          :key="x.name"
          @click="changeTab(x.name)"
        >
          {{ x.name }}
          <template v-if="x.badge">
            <div class="fts-inline-spacer-1"></div>
            <fts-release-badge v-bind="x.badge" class="fts-float-right" />
          </template>
        </div>
      </div>
      <!-- integrations -->
      <div class="user-integration" v-if="section === 'integration'">
        <div>
          <div class="user-profile-form-label user-integration-label">API Key</div>
          <div class="user-integration-warning">
            Warning: Do not pass this key to any untrusted party. Generating new api
            key will void existing keys, existing integrations have to be replaced
            with new key.
          </div>

          <div class="user-integration-api-key-wrapper" v-if="formData.apiKey">
            <div class="user-integration-api-key">
              {{formData.apiKey}}
              <div class="user-integration-copy-hint" v-if="linkCopied">Copied</div>
            </div>
            <fts-btn
              icon
              text
              v-clipboard:copy="formData.apiKey"
              v-clipboard:success="clipboardOnCopy"
              v-clipboard:error="clipboardOnError"
            >
              <font-awesome-icon :icon="['far', 'clone']" />
            </fts-btn>
          </div>

          <div class="user-integration-generate-action">
            <fts-btn
              outline
              color="primary"
              text-transform-init
              :loading="apiKeyLoading"
              @click.native.prevent="getAPIKey"
            >
              Generate API Key
            </fts-btn>
          </div>
        </div>
      </div>
      <!-- password -->
      <form autocomplete="off" v-else-if="section === 'password'">
        <fts-form-element
          v-for="x in fields"
          :item="x"
          :formData="formData"
          :validator="$v.formData"
          :key="x.id"
          comfortDesign
        />
      </form>
      <!-- preference -->
      <div class="user-preference" v-else-if="section == 'preferences'">
        <!-- reading (FTR00375): this person's own text size and contrast, kept on this device -->
        <div class="user-preference-reading" v-if="readingVisible">
          <div class="user-preference-reading__title">
            Reading
            <fts-release-badge v-bind="readingBadge" v-if="readingBadge" />
          </div>
          <div class="user-preference-reading__helper">
            Saved on this device, for you only. Other people who sign in here keep their own.
            Set it again on your other devices.
          </div>
          <div class="user-preference-reading__group" role="group" aria-labelledby="reading-size-label">
            <div class="user-preference-reading__label" id="reading-size-label">Text size</div>
            <div class="user-preference-reading__options">
              <button
                type="button"
                class="user-preference-reading__option"
                :class="{ 'user-preference-reading__option--active': readingDisplay.textSize === x.id }"
                :aria-pressed="readingDisplay.textSize === x.id ? 'true' : 'false'"
                v-for="x in readingSizes"
                :key="x.id"
                @click="setReading({ textSize: x.id })"
              >
                <span class="user-preference-reading__sample" :class="'user-preference-reading__sample--' + x.id" aria-hidden="true">Aa</span>
                <span>{{ x.name }}</span>
              </button>
            </div>
            <div class="user-preference-reading__note">{{ readingSizeNote }}</div>
          </div>
          <div class="user-preference-reading__group" role="group" aria-labelledby="reading-contrast-label">
            <div class="user-preference-reading__label" id="reading-contrast-label">Contrast</div>
            <div class="user-preference-reading__options">
              <button
                type="button"
                class="user-preference-reading__option user-preference-reading__option--wide"
                :class="{ 'user-preference-reading__option--active': readingDisplay.contrast === x.id }"
                :aria-pressed="readingDisplay.contrast === x.id ? 'true' : 'false'"
                v-for="x in readingContrasts"
                :key="x.id"
                @click="setReading({ contrast: x.id })"
              >
                <span class="user-preference-reading__option-name">{{ x.name }}</span>
                <span class="user-preference-reading__option-desc">{{ x.description }}</span>
              </button>
            </div>
          </div>
          <fts-toggle-switch
            v-model="readingFollowDevice"
            :size="16"
            label-dir="left"
            mat-class
            class="user-preference-sync-os user-preference-reading__follow"
          >
            <div slot="label" class="user-preference-sync-os__label">
              <div class="user-preference-reading__label">Follow my device's contrast setting</div>
              <div class="user-preference-reading__note">
                When your computer or phone asks apps for more contrast, use High.
              </div>
            </div>
          </fts-toggle-switch>
        </div>
        <!-- sync with OS -->
        <fts-toggle-switch 
          v-if="showAppearance"
          v-model="preferenceSyncOS"
          :size="16"
          label-dir="left"
          mat-class
          class="user-preference-sync-os"
        >
          <div slot="label" class="user-preference-sync-os__label">
            <div class="user-preference-sync-os-label">
              Sync with system setting
            </div>
            <div class="user-preference-sync-os-helper">
              Automatically switch between light and dark themes when your system does.
            </div>
          </div>
        </fts-toggle-switch>
        <!-- appearance -->
        <div class="user-profile-form-label" v-if="showAppearance">Appearance</div>
        <div class="fts-row gutters fts-justify-content-center fts-justify-content-md-start user-preference-theme" v-if="showAppearance">
          <div class="fts-col-8 fts-col-md-6 user-preference-theme-wrapper">
            <div class="user-preference-theme-card theme-light">
              <div class="user-preference-theme-card__header">Inistate</div>
              <div class="user-preference-theme-card__content"></div>
            </div>
            <div
              class="user-preference-action"
              :class="{'user-preference-action--disabled': preferenceSyncOS}"
              @click="!preferenceSyncOS && (preferenceTheme = 'light')"
            >       
              <font-awesome-icon
                :icon="['fas', 'check-circle']"
                fixed-width
                class="user-preference-action-checkbox active" 
                v-if="preferenceTheme === 'light'"
              />
              <font-awesome-icon
                :icon="['fas', 'circle']"
                fixed-width
                class="user-preference-action-checkbox"         
                v-else
              />
              <div class="user-preference-action-label">Light</div>
            </div>
            <div class="user-preference-helper" v-if="preferenceSyncOS && preferenceTheme === 'light'">
              Chosen automatically by your OS
            </div>
          </div>
          <div class="fts-col-8 fts-col-md-6 user-preference-theme-wrapper">
            <div class="user-preference-theme-card theme-dark">
              <div class="user-preference-theme-card__header">Inistate</div>
              <div class="user-preference-theme-card__content"></div>
            </div>
            <div
              class="user-preference-action"
              :class="{'user-preference-action--disabled': preferenceSyncOS}"
              @click="!preferenceSyncOS && (preferenceTheme = 'dark')"
            >       
              <font-awesome-icon
                :icon="['fas', 'check-circle']"
                fixed-width
                class="user-preference-action-checkbox active" 
                v-if="preferenceTheme === 'dark'"
              />
              <font-awesome-icon
                :icon="['fas', 'circle']"
                fixed-width
                class="user-preference-action-checkbox"
                v-else
              />
              <div class="user-preference-action-label">Dark</div>
            </div>
            <div class="user-preference-helper" v-if="preferenceSyncOS && preferenceTheme === 'dark'">
              Chosen automatically by your OS
            </div>
          </div>
        </div>
        <!-- colors -->
        <div class="user-profile-form-label" v-if="showAppearance">Colors</div>
        <div class="fts-row gutters fts-justify-content-center fts-justify-content-md-start user-preference-color" v-if="showAppearance">
          <div
            class="fts-col-8 fts-col-md-6 user-preference-color-wrapper"
            v-for="color in preferenceColors"
            :key="color.id"
          >
            <div
              class="user-preference-color-card"
              :class="color.id"
            >
              <div class="user-preference-color-card__sidenav">
                <div class="sidenav__body">
                  <div class="sidenav-logo"></div>
                  <div class="sidenav-item-wrapper sidenav-item-wrapper--active">
                    <div class="sidenav-item"></div>
                  </div>
                  <div class="sidenav-item-wrapper">
                    <div class="sidenav-item"></div>
                  </div>
                  <div class="sidenav-apex-wrapper">
                    <div class="sidenav-apex"></div>
                  </div>
                </div>
                <div class="sidenav__footer">
                  <div class="sidenav-profile"></div>
                </div>
              </div>
              <div class="user-preference-color-card__drawer">
                <div class="drawer__body">
                  <div class="drawer-workspace"></div>
                  <div class="drawer-item-wrapper drawer-item-wrapper--active">
                    <div class="drawer-item"></div>
                  </div>
                  <div class="drawer-item-wrapper">
                    <div class="drawer-item"></div>
                  </div>
                </div>
                <div class="drawer__footer">
                  <div class="drawer-user"></div>
                  <div class="drawer-username"></div>
                </div>
              </div>
            </div>
            <div
              class="user-preference-action"
              @click="preferenceColor = color.id"
            >       
              <font-awesome-icon 
                :icon="['fas', 'check-circle']" 
                fixed-width 
                class="user-preference-action-checkbox active" 
                v-if="preferenceColor === color.id"
              />
              <font-awesome-icon 
                :icon="['fas', 'circle']" 
                fixed-width 
                class="user-preference-action-checkbox"
                v-else 
              />
              <div class="user-preference-action-label">{{color.name}}</div>
            </div>
          </div>
        </div>
      </div>
      <!-- profile -->
      <div class="fts-row gutters" v-else>
        <div class="fts-col fts-col-md-5 comfort-form-design">
          <div class="fts-col fts-form-group">
            <div class="profile-photo">
              <div class="profile-photo-container">
                <div class="profile-photo-wrapper" id="pick-avatar">
                  <img :src="profilePhoto" />
                </div>
                <div class="profile-photo-icon">
                  <fts-btn
                    sm
                    icon
                    color="primary"
                    box-shadow-none
                    id="pick-avatar-icon"
                    :loading="uploaderUploading"
                    @click="openUploader">
                    <font-awesome-icon :icon="['fas', 'plus']" />
                  </fts-btn>
                </div>
                <div class="profile-photo-icon">
                  <fts-btn
                    sm
                    icon
                    box-shadow-none
                    :disabled="uploaderUploading"
                    @click="removeProfilePicture"
                    v-if="formData.profilePictureUrl">
                    <font-awesome-icon :icon="['fas', 'times']" />
                  </fts-btn>
                </div>
              </div>
            </div>
          </div>

          <avatar-cropper
            trigger="#pick-avatar"
            :upload-url="fileUploadURL"
            :upload-form-data="{ profile: true }"
            :labels="{ submit: 'Done', cancel: 'Cancel' }"
            :output-options="{
              width: 512,
              height: 512,
            }"
            ref="avatarCropper"
            @uploading="uploaderUploadingHandler"
            @completed="uploaderCompletedHandler"
            @error="uploaderErrorHandler"
            @uploaded="uploaderUploadedHandler"
          />

          <div class="user-roles-container" v-if="userRoles">
            <div class="user-roles">
              <fts-badge v-for="role in userRoles" :key="role" size="sm">
                {{role}}
              </fts-badge>
            </div>
          </div>
        </div>
        <div class="fts-col fts-col-md-7 comfort-form-design">
          <fts-form-element
            v-for="x in fields"
            :item="x"
            :formData="formData"
            :validator="$v.formData"
            :key="x.id"
            comfortDesign
          />
          <div class="fts-form-group">
            <fts-toggle-switch
              :form-data="formData"
              :item="{ fieldName: 'onBehalf' }"
              full-width
              mat-class
              label-dir="left"
              :size="16"
            >
              <div slot="label" slot-scope="{ reference }" class="toggle-switch__label">
                <fts-form-label :for="reference">On Behalf</fts-form-label>
              </div>
            </fts-toggle-switch>
          </div>
          <fts-transition-expand>
            <div v-if="formData.onBehalf">
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'startDate',
                  type: 5
                }"
                comfort-design
              />
              <fts-form-element
                :form-data="formData"
                :item="{
                  fieldName: 'endDate',
                  type: 5
                }"
                comfort-design
              />
              <fts-form-element
                :form-data="formData"
                :item="{
                  displayName: 'On Behalf Of',
                  fieldName: 'onBehalfUsername'
                }"
                comfort-design
              >
                <fts-select
                  slot="formElement"
                  slot-scope="{ formData, item }"
                  v-model="formData[item.fieldName]"
                  label="displayName"
                  reduce-key="username"
                  :options="users"
                  comfort-design
                >
                  <fts-profile
                    slot="selected-option-container"
                    :user-name="formData.onBehalfUsername"
                    :user-list="users"
                  />
                  <fts-profile
                    slot="option"
                    slot-scope="{ option }"
                    :profile="option"
                    :user-list="users"
                  />
                </fts-select>
              </fts-form-element>
            </div>
          </fts-transition-expand>
        </div>
      </div>
      <!-- build version -->
      <div class="app-build-version" v-if="buildVersion">
        Version {{buildVersion}}
      </div>
    </template>
    <!-- footer:left -->
    <template #footerLeft>
      <fts-btn
        text
        :disabled="loading"
        @click.native.prevent="logout"
      >
        <font-awesome-icon :icon="['fas', 'sign-out-alt']" /> Log Out
      </fts-btn>
    </template>
    <!-- footer:right -->
    <template #footerRight>
      <fts-btn
        text
        :disabled="loading"
        @click.native.prevent="beforeClose"
      >
        <font-awesome-icon :icon="['fas', 'times']" /> Close
      </fts-btn>

      <fts-btn
        color="primary"
        :loading="loading"
        @click.native.prevent="updateProfile"
        v-if="section === 'profile'"
      >
        <font-awesome-icon :icon="['fas', 'check']" /> Update
      </fts-btn>

      <fts-btn
        color="primary"
        :loading="loading"
        @click.native.prevent="changePassword"
        v-if="section === 'password'"
      >
        <font-awesome-icon :icon="['fas', 'check']" /> Update
      </fts-btn>
    </template>
  </fts-client-modal>
</template>

<script>
import Vue from 'vue';
import Vuelidate from 'vuelidate';
Vue.use(Vuelidate);
import { sameAs } from 'vuelidate/lib/validators';

import webAppConfig from '../commons/webApp.config';
import commons from '../commons/commons';
import { builderNotAvailableMessage } from '../commons/message';
import { passwordValidator } from '../commons/validator';
import { authorizationStore } from '../store/authorizationStore';
import { store as canvasDesignStore } from '../store/canvasDesignStore';
import { clientStore } from '../store/clientStore';
import { ReleaseStage } from '../commons/enum';
import { READING_STAGE, readDisplay, writeDisplay, applyDisplay, isCustomised } from '../commons/readability';
import { stageAllows } from '../settings/settingsStage';

import changesets from 'diff-json';
import AvatarCropper from 'vue-avatar-cropper';
import VueClipboard from 'vue-clipboard2';
Vue.use(VueClipboard);

import FTSBadge from './FTSBadge';
import FTSBtn from './FTSBtn';
import FTSClientModal from './FTSClientModal';
import FTSFormElement from './FTSFormElement';
import FTSFormLabel from './FTSFormLabel';
import FTSProfile from './FTSProfile';
import FTSReleaseBadge from './FTSReleaseBadge';
import FTSSelect from './FTSSelect';
import FTSToggleSwitch from './FTSToggleSwitch';
import FTSTransitionExpand from './FTSTransitionExpand';

const SECTION_DEFINITIONS = [
  { name: "profile" },
  { name: "password" },
  { name: "integration" },
  {
    name: "preferences",
    // Appearance and Colors live in the Client shell only (and for development); the
    // Reading block (FTR00375) opens the section in every shell while it is in stage.
    hidden: ({ client, isDevelopment, isLite, reading }) =>
      !reading && (isLite === true || (client !== true && !isDevelopment)),
    badge: ({ client, isDevelopment }) =>
      client !== true && isDevelopment ? { dev: true } : null,
  },
];

export default {
  components: {
    AvatarCropper,
    "fts-badge": FTSBadge,
    "fts-btn": FTSBtn,
    "fts-client-modal": FTSClientModal,
    "fts-form-element": FTSFormElement,
    "fts-form-label": FTSFormLabel,
    "fts-profile": FTSProfile,
    "fts-release-badge": FTSReleaseBadge,
    "fts-select": FTSSelect,
    "fts-toggle-switch": FTSToggleSwitch,
    "fts-transition-expand": FTSTransitionExpand
  },
  props: {
    client: Boolean,
    isLite: Boolean
  },
  data: () => ({
    section: "profile",
    formData: {},
    originalData: null,
    resolve: null,
    preference: {},
    /* reading (FTR00375): Default is today's look; the rest is this person's own choice */
    readingDisplay: { textSize: 'default', contrast: 'default', followDevice: false },
    readingSizes: [
      { id: 'default', name: 'Default', note: 'Default is the standard size everyone sees.' },
      { id: 'large', name: 'Large', note: 'Form labels and values at 16 px, on every screen width.' },
      { id: 'larger', name: 'Larger', note: 'Form labels and values at 18 px, on every screen width.' },
    ],
    readingContrasts: [
      { id: 'default', name: 'Default', description: 'The colours everyone sees.' },
      { id: 'high', name: 'High', description: 'Darker text and greys, visible field borders, a thick focus ring.' },
    ],
    loading: false,
    apiKeyLoading: false,
    /* uploader */
    uploaderUploading: false,
    linkCopied: false,
    /* preference colors */
    preferenceColors: [
      { id: 'fts-color--default', name: 'Default' },
      { id: 'fts-color--client', name: 'Client'},
      { id: 'fts-color--studio', name: 'Studio' },
      { id: 'fts-color--immersive', name: 'Immersive' },
      { id: 'fts-color--violet', name: 'Violet' },
    ],
    colorSchemeMedia : null,
    /* build version */
    buildVersion: webAppConfig.buildVersion,
  }),
  validations() {
    let formData = {};
    formData = commons.getFormValidators(this.fields);
    return { formData };
  },
  methods: {
    async show(payload) {
      /* reading: this person's choice on this device - first, because whether Preferences
         is open to them can depend on it (readingVisible) */
      this.readingDisplay = readDisplay(authorizationStore.getters.userName);
      /* set section */
      if (payload && payload.section && this.getSections.some((x) => x.name === payload.section)) {
        this.section = payload.section
      }
      /* clone profile */
      this.formData = _.cloneDeep(this.profile);
      /* sync with OS */
      if (localStorage['fetias-client-theme-sync-os'])
        this.$set(this.preference, 'preferenceSyncOS', true);
      else
        this.$set(this.preference, 'preferenceSyncOS', false);
      /* appearance */
      if (localStorage['fetias-client-theme'] && localStorage['fetias-client-theme'] === 'dark')
        this.$set(this.preference, 'theme', 'dark');
      else
        this.$set(this.preference, 'theme', 'light');
      /* color */
      const color = localStorage['fetias-client-color'];
      if (this.preferenceColors.findIndex(x => x.id === color) > -1)
        this.$set(this.preference, 'color', color);
      else
        this.$set(this.preference, 'color', 'fts-color--default');
      /* window match media */
      this.colorSchemeMedia = window.matchMedia('(prefers-color-scheme: dark)');
      this.colorSchemeMedia.addEventListener('change', this.colorSchemeEvent)

      return new Promise((resolve) => {
        this.resolve = resolve;
        this.$refs.ftsClientModal.show({
          beforeCloseHandler: () => {
            const diff = changesets.diff(this.formData, this.originalData);
            if (diff && diff.length) { 
              this.$modal.show("dialog", {
                title: "Confirm discard changes",
                text: "Discard all changes?",
                buttons: [
                  { title: "Cancel" },
                  {
                    title: "Discard",
                    default: true,
                    handler: () => {
                      this.$modal.hide("dialog");
                      this.reset();
                    },
                  },
                ],
              });
            } else {
              this.reset();
            }
          },
        });
      
        this.$nextTick(() => {
          this.originalData = _.cloneDeep(this.formData);
        });
      });
    },
    async updateProfile() {
      this.$v.formData.$touch();
      if (this.$v.formData.$invalid) {
        this.$toasted.global.invalidFormValidate();
      } else {
        this.loading = true;
        try {
          await authorizationStore.dispatch("updateUserProfile", {
            displayName: this.formData.displayName,
            firstName: this.formData.firstName,
            lastName: this.formData.lastName,
            email: this.formData.email,
            phoneNumber: this.formData.phoneNumber,
            profilePictureUrl: this.formData.profilePictureUrl,
            onBehalf: this.formData.onBehalf,
            startDate: this.formData.startDate,
            endDate: this.formData.endDate,
            onBehalfUsername: this.formData.onBehalfUsername
          });
          this.$toasted.success("Profile updated successfully.");
          this.reset();
        } catch (err) {
          console.error(err);
          this.$toasted.global.error({
            response: err.response,
          });
        }
        this.loading = false;
      }
    },
    async changePassword() {
      this.$v.formData.$touch();
      if (!this.$v.formData.$invalid) {
        this.loading = true;
        try {
          await authorizationStore.dispatch("changePassword", {
            confirmNewPassword: this.formData.confirmNewPassword,
            currentPassword: this.formData.currentPassword,
            newPassword: this.formData.newPassword,
          });
          this.$toasted.success("Password updated successfully.");
          this.reset();
        } catch (err) {
          console.error(err);
          this.$toasted.global.error({
            response: err.response,
          });
        } 
        this.loading = false;
      }
    },
    beforeClose() {
      this.$refs.ftsClientModal.beforeClose();
    },
    reset() {
      this.$v.$reset();
      this.$refs.ftsClientModal.close();
      this.offColorSchemeMedia();
      this.$nextTick(() => {
        Object.assign(this.$data, this.$options.data.apply(this));
      });
    },
    logout() {
      if (commons.builderMode) {
        this.$toasted.info(builderNotAvailableMessage('Log Out'));
      } else {
        authorizationStore.dispatch("logout");
      }
    },
    /* reading: save on this device for this person, then repaint at once */
    setReading(change) {
      const userName = authorizationStore.getters.userName;
      this.readingDisplay = writeDisplay(userName, { ...this.readingDisplay, ...change });
      applyDisplay(userName);
    },
    colorSchemeEvent(event) {
      if (this.preferenceSyncOS) {
        this.preferenceTheme = event.matches ? 'dark' : 'light';
      }
    },
    offColorSchemeMedia() {
      this.colorSchemeMedia && this.colorSchemeMedia.removeEventListener('change', this.colorSchemeEvent);
    },
    changeTab(item) {
      function next(vm) {
        vm.section = item;
        vm.$nextTick(() => {
          vm.originalData = _.cloneDeep(vm.formData);
        });
      }
      const diff = changesets.diff(this.formData, this.originalData);
      if (diff && diff.length) {
        this.$modal.show("dialog", {
          title: "Confirm discard changes",
          text: "Discard all changes?",
          buttons: [
            { title: "Cancel" },
            {
              title: "Discard",
              default: true,
              handler: () => {
                this.formData = _.cloneDeep(this.originalData);
                this.$modal.hide("dialog");
                next(this);
              },
            },
          ],
        });
      } else {
        next(this);
      }
    },
    /* clipboard */
    clipboardOnCopy() {
      if (!this.linkCopied) {
        this.linkCopied = true;
        setTimeout(() => {
          this.linkCopied = false;
        }, 2000);
      }
    },
    clipboardOnError() {
      console.error("ClipboardOnError");
    },
    /* API Key */
    async getAPIKey() {
      this.apiKeyLoading = true;
      try {
        const res = await authorizationStore.dispatch("getAPIKey");
        authorizationStore.dispatch("setAPIKey", res.data);
        this.formData.apiKey = res.data;
        this.originalData.apiKey = _.cloneDeep(this.formData.apiKey);
      } catch(err) {
        console.error(err);
      } finally {
        this.apiKeyLoading = false;
      }
    },
    /* uploader handler */
    openUploader() {
      this.$refs.avatarCropper.pickImage();
    },
    uploaderUploadingHandler(form, xhr) {
      this.uploaderUploading = true;
    },
    uploaderCompletedHandler(res, form, xhr) {
      this.uploaderUploading = false;
    },
    uploaderErrorHandler(msg, type, xhr) {
      this.$toasted.error(`${msg}`);
    },
    uploaderUploadedHandler(res, form, xhr) {
      this.$set(this.formData, "profilePictureUrl", res.url);
    },
    removeProfilePicture() {
      this.$delete(this.formData, "profilePictureUrl");
    },
  },
  computed: {
    fileUploadURL() {
      return webAppConfig.fileUploadURL;
    },
    isDevelopment() {
      return authorizationStore.getters.isDevelopment;
    },
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    /* reading (FTR00375) rides the release ladder: Alpha = staff, Beta = Beta Mode, GA = all */
    readingAllowed() {
      return stageAllows(READING_STAGE, {
        enabled: true,
        internal: authorizationStore.getters.isInternal,
        beta: authorizationStore.getters.isBeta,
        workspaceBeta: clientStore.getters.workspaceBeta,
      });
    },
    /* in stage, or already changed something: a choice that is applied can always be undone */
    readingVisible() {
      return this.readingAllowed || isCustomised(this.readingDisplay);
    },
    readingBadge() {
      if (READING_STAGE === ReleaseStage.ALPHA) return { alpha: true };
      if (READING_STAGE === ReleaseStage.BETA) return { stage: ReleaseStage.BETA };
      return null;
    },
    readingSizeNote() {
      const size = this.readingSizes.find((x) => x.id === this.readingDisplay.textSize);
      return size ? size.note : '';
    },
    readingFollowDevice: {
      get() {
        return this.readingDisplay.followDevice;
      },
      set(val) {
        this.setReading({ followDevice: Boolean(val) });
      }
    },
    /* Appearance and Colors are applied by the Client shell only */
    showAppearance() {
      return this.isLite !== true && (this.client === true || Boolean(this.isDevelopment));
    },
    getSections() {
      const context = {
        client: this.client,
        isDevelopment: this.isDevelopment,
        isLite: this.isLite,
        reading: this.readingVisible,
      };

      return SECTION_DEFINITIONS
        .filter((section) => !(section.hidden && section.hidden(context)))
        .map((section) => ({
          ...section,
          badge: section.badge ? section.badge(context) : null,
        }));
    },
    preferenceTheme: {
      get() {
        return this.preference.theme;
      },
      set(val) {
        this.$set(this.preference, 'theme', val);
        localStorage.setItem('fetias-client-theme', val);
        if (this.preference.theme === 'dark') {
          document.body.classList.add('dark');
        } else {
          document.body.classList.remove('dark');
        }
      }
    },
    preferenceColor: {
      get() {
        return this.preference.color;
      },
      set(val) {
        this.$set(this.preference, 'color', val);
        localStorage.setItem("fetias-client-color", val);
        let classList = document.body.getElementsByClassName('fts-client')[0].classList;
        this.preferenceColors.forEach(x => {
          if (classList.contains(x.id)) classList.remove(x.id);
        });
        classList.add(val);
      }
    },
    preferenceSyncOS: {
      get() {
        return this.preference.preferenceSyncOS;
      },
      set(val) {
        this.$set(this.preference, 'preferenceSyncOS', val);
        if (val) {
          localStorage.setItem("fetias-client-theme-sync-os", val);
          if (window.matchMedia('(prefers-color-scheme: dark)').matches)
            this.preferenceTheme = "dark";
          else
            this.preferenceTheme = "light";
        } else {
          localStorage.removeItem("fetias-client-theme-sync-os")
        }
      }
    },
    fields() {
      if (this.section === "profile")
        return [
          {
            type: 0,
            fieldName: "userName",
            readOnly: true,
          },
          {
            type: 0,
            fieldName: "displayName",
            required: true,
          },
          {
            type: 0,
            fieldName: "firstName",
          },
          {
            type: 0,
            fieldName: "lastName",
          },
          {
            type: 26,
            fieldName: "phoneNumber",
          },
          {
            type: 25,
            fieldName: "email",
          },
        ];
      else if (this.section === "password")
        return [
          {
            input: "password",
            fieldName: "currentPassword",
            required: true,
          },
          {
            input: "password",
            fieldName: "newPassword",
            required: true,
            validator: {
              password: passwordValidator,
            },
          },
          {
            input: "password",
            fieldName: "confirmNewPassword",
            required: true,
            validator: {
              sameAs: sameAs(() => this.formData.newPassword),
            },
          },
        ];
      else return [];
    },
    userName() {
      return authorizationStore.state.userName;
    },
    userRoles() {
      try {
        return authorizationStore.getters.userRoles.split(",");
      } catch(err) {
        console.error("UserRoles", err);
      }
    },
    profile() {
      return authorizationStore.getters.userProfile;
    },
    profilePhoto() {
      if (this.formData.profilePictureUrl)
        return webAppConfig.baseURL && this.formData.profilePictureUrl.startsWith('/') ?
          `${webAppConfig.baseURL}${this.formData.profilePictureUrl}` : this.formData.profilePictureUrl;
      else
        return commons.defaultProfileImage;
    },
    users() {
      return this.$route.name === 'studio' || this.$route.name === 'builder' ?
        canvasDesignStore.getters.workspaceUsers :
        clientStore.getters.workspaceUsers;
    }
  },
  beforeDestroy() {
    this.offColorSchemeMedia();
  }
};
</script>

<style lang="scss">
@use "sass:map";
@use "./src/assets/scss/vars" as *;
@use "./src/assets/scss/colors" as *;

.fts-user-profile-modal {
  &.preferences {
    background-color: transparent !important;
    > .fts-modal-wrapper {
      > .fts-modal-container {
        box-shadow: var(--fts-elevation-4);
      }
    }
  }
  .user-profile-modal__body {
    padding-top: 0 !important;
  }
  /* tabs */
  .user-profile-tabs {
    @include user-select-none;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    position: sticky;
    top: 0;
    margin: 0 -1rem 0.375rem;
    padding: 1rem;
    background-color: var(--fts-card-background);
    z-index: 2;
    .user-profile-tab {
      padding-bottom: 0.175rem;
      border-bottom: 2px solid transparent;
      font-weight: 500;
      transition: all 0.3s ease-in-out;
      cursor: pointer;
      text-transform: capitalize;
      &:not(:last-child) {
        margin-right: 1rem;
      }
      &:not(.active) {
        @include theme() {
          color: rgba(theme-get('fts-gray-400'), 0.65);
        }
      }
      &:hover {
        @include theme() {
          color: rgba(theme-get('fts-gray-400'), 0.85);
        }
      }
      &.active {
        border-color: var(--fts-gray-400);
        color: var(--fts-gray-400);
      }
    }
  }
  /* form */
  .user-profile-form-label {
    margin-bottom: .575rem;
    font-size: .875rem;
    color: var(--fts-form-label);
  }
  /* profile photo */
  .profile-photo {
    display: flex;
    flex-direction: column;
    align-items: center;
    .profile-photo-container {
      position: relative;
      .profile-photo-wrapper {
        height: 130px;
        width: 130px;
        margin-bottom: 0.75rem;
        border: 1px solid var(--fts-border);
        border-radius: 50%;
        overflow: hidden;
        cursor: pointer;
        > img {
          height: 100%;
          width: 100%;
        }
      }
      .profile-photo-icon {
        position: absolute;
        bottom: 16px;
        right: -3px;
        padding: 0.135rem;
        border-radius: 50%;
        background: var(--fts-card-background);
      }
    }
  }
  /* user roles */
  .user-roles-container {
    margin-bottom: 2rem;
    .user-roles {
      @include flex-gap($display: flex, $gap: 0.375rem);
    }
  }
  /* api key */
  .user-integration {
    .user-integration-label {
      margin-bottom: .375rem;
    }
    .user-integration-warning {
      font-size: .75rem;
      color: $warningColor;
      line-height: 1.25;
    }
    .user-integration-api-key-wrapper {
      display: flex;
      align-items: center;
      margin-top: 1rem;
      padding: .375rem .75rem;
      border-radius: .375rem;
      background-color: var(--fts-gray-100);
      .user-integration-api-key {
        position: relative;
        flex: 1;
        margin-right: .375rem;
        font-size: .75rem;
        word-break: break-all;
      }
      .user-integration-copy-hint {
        @include absolute-center-y;
        right: 0;
        padding: .375rem;
        box-shadow: var(--fts-elevation-3);
        border-radius: .25rem;
        background: var(--fts-popover-alt-background);
        color: var(--fts-text);
        font-size: .75rem;
      }
    }
    .user-integration-warning + 
    .user-integration-generate-action {
      margin-top: 1rem;
    }
    .user-integration-api-key-wrapper + 
    .user-integration-generate-action {
      margin-top: .575rem;
    }
  }
  /* override */
  .avatar-cropper {
    .avatar-cropper-overlay {
      margin-top: 55px;
      align-items: flex-start;
    }
    .avatar-cropper-close {
      text-decoration: none;
    }
    .avatar-cropper-container {
      .avatar-cropper-image-container {
        /* Circle Crop */
        .cropper-crop-box {
          border-radius: 50%;
          .cropper-view-box {
            border-radius: inherit;
            box-shadow: 0 0 0 1px #39f;
            outline: 0;
          }
          .cropper-face {
            background-color: inherit;
          }
          .cropper-dashed,
          .cropper-point.point-se,
          .cropper-point.point-sw,
          .cropper-point.point-nw,
          .cropper-point.point-ne,
          .cropper-line {
            display: none;
          }
          .cropper-view-box {
            outline: inherit;
          }
        }
      }
      .avatar-cropper-footer {
        .avatar-cropper-btn {
          &:hover {
            background: var(--fts-primary);
          }
        }
      }
    }
  }
  /* preference */
  .user-preference {
    /* reading (FTR00375): the one place someone goes to make text readable, so its own
       text uses the full text colour, never the light form-label grey */
    .user-preference-reading {
      margin-bottom: 2rem;
      padding-bottom: 1.5rem;
      border-bottom: 1px solid var(--fts-border);
      .user-preference-reading__title {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 0.25rem;
        font-size: 1rem;
        line-height: 1.5rem;
        font-weight: 600;
        color: var(--fts-text);
      }
      .user-preference-reading__helper {
        margin-bottom: 1.25rem;
        font-size: 0.875rem;
        line-height: 1.25rem;
        color: var(--fts-text);
      }
      .user-preference-reading__group {
        margin-bottom: 1.25rem;
      }
      .user-preference-reading__label {
        margin-bottom: 0.5rem;
        font-size: 0.875rem;
        line-height: 1.25rem;
        font-weight: 500;
        color: var(--fts-text);
      }
      .user-preference-reading__note {
        margin-top: 0.375rem;
        font-size: 0.8125rem;
        line-height: 1.125rem;
        color: var(--fts-gray-700);
      }
      .user-preference-reading__options {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
      }
      .user-preference-reading__option {
        display: inline-flex;
        align-items: center;
        gap: 0.625rem;
        min-width: 7.5rem;
        min-height: 2.75rem;
        padding: 0 1rem;
        border: 1px solid var(--fts-border);
        border-radius: 0.5rem;
        background-color: transparent;
        font-family: inherit;
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--fts-text);
        cursor: pointer;
        transition: background-color 0.12s ease, border-color 0.12s ease;
        &:hover {
          background-color: var(--fts-gray-50);
        }
        &.user-preference-reading__option--active {
          border-color: var(--fts-primary);
          box-shadow: inset 0 0 0 1px var(--fts-primary);
          background-color: rgba(var(--fts-primary-rgb), 0.08);
        }
        &.user-preference-reading__option--wide {
          flex: 1 1 14rem;
          flex-direction: column;
          align-items: flex-start;
          justify-content: center;
          gap: 0.125rem;
          padding-top: 0.625rem;
          padding-bottom: 0.625rem;
          text-align: left;
        }
      }
      .user-preference-reading__option-name {
        font-weight: 600;
      }
      .user-preference-reading__option-desc {
        font-size: 0.8125rem;
        line-height: 1.125rem;
        font-weight: 400;
        color: var(--fts-gray-700);
      }
      .user-preference-reading__sample {
        width: 1.75rem;
        line-height: 1;
        font-weight: 600;
        &.user-preference-reading__sample--default { font-size: 0.875rem; }
        &.user-preference-reading__sample--large { font-size: 1.0625rem; }
        &.user-preference-reading__sample--larger { font-size: 1.25rem; }
      }
      .user-preference-reading__follow {
        margin-bottom: 0;
      }
    }
    .user-preference-action {
      @include user-select-none;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin-top: .75rem;
      font-size: 1rem;
      cursor: pointer;
      &.user-preference-action--disabled {
        cursor: not-allowed;
        .user-preference-action-checkbox {
          color: var(--fts-disabled) !important;
        }
      }
      .user-preference-action-checkbox {
        margin-right: .375rem;
        color: var(--fts-gray-200);
        &.active {
          color: var(--fts-success);
        }
      }
      .user-preference-action-label {
        color: var(--fts-text);
      }
    }
    .user-preference-helper {
      margin-top: .375rem;
      font-size: .875rem;
      color: var(--fts-gray-400);
    }
    .user-preference-sync-os {
      display: flex;
      align-items: flex-start;
      margin-bottom: 2rem;
      .user-preference-sync-os__label {
        flex: 1;
        font-size: 0.875rem;
        .user-preference-sync-os-label {
          margin-bottom: .175rem; 
          color: var(--fts-form-label);
        }
        .user-preference-sync-os-helper {
          color: var(--fts-form-helper);
        }
      }
    }
    .user-preference-theme {
      .user-preference-theme-wrapper {
        margin-bottom: 2rem;
        text-align: center;
        font-size: 1rem;
        .user-preference-theme-card {
          border-radius: .75rem;
          box-shadow: var(--fts-elevation-1);
          &.theme-light {
            background: #fff;
            color: #000;
            .user-preference-theme-card__header {
              background: #eee;
            }
          }
          &.theme-dark {
            background: #212121;
            color: #eee;
            .user-preference-theme-card__header {
              background: #424242;
            }
          }
          .user-preference-theme-card__header {
            padding: .75rem 1rem;
            border-top-left-radius: inherit;
            border-top-right-radius: inherit;
            font-weight: 500;
            text-align: left;
          }
          .user-preference-theme-card__content {
            height: 4rem;
          }
        }
      }
    }
    .user-preference-color {
      .user-preference-color-wrapper {
        margin-bottom: 2rem;
        text-align: center;
        .user-preference-color-card {
          height: 9rem;
          display: flex;
          border-radius: .75rem;
          box-shadow: var(--fts-elevation-1);
          .user-preference-color-card__sidenav {
            width: 40px;
            display: flex;
            flex-direction: column;
            border-top-left-radius: inherit;
            border-bottom-left-radius: inherit;
            .sidenav__body {
              flex: 1;
              display: flex;
              flex-direction: column;
              align-items: center;
              .sidenav-logo {
                margin: 0.375rem 0 0.575rem;
                height: 1rem;
                width: 1rem;
                border-radius: 0.15rem;
                background: #fff;
              }
              .sidenav-item-wrapper {
                margin-bottom: .375rem;
                padding: .2rem;
                border-radius: .15rem;         
                .sidenav-item {
                  height: .875rem;
                  width: .875rem;
                  border-radius: inherit;       
                }
              }
              .sidenav-apex-wrapper {
                flex: 1;
                display: flex;
                align-items: flex-end;
                .sidenav-apex {
                  height: .875rem;
                  width: .875rem;
                  margin-bottom: .375rem;
                  border-radius: .15rem;
                }
              }
            }
            .sidenav__footer {
              position: relative;
              height: calc(#{0.375rem + 0.375rem + 0.575rem + 0.175rem + 0.475rem});
              .sidenav-profile {
                @include absolute-center-middle;
                height: .975rem;
                width: .975rem;
                border-radius: 10em;
              }
            }
          }
          .user-preference-color-card__drawer {
            flex: 1;
            display: flex;
            flex-direction: column;
            border-top-right-radius: inherit;
            border-bottom-right-radius: inherit;       
            .drawer__body {
              flex: 1;
              .drawer-workspace {
                height: .675rem;
                width: 30%;
                margin: 0.575rem;
                border-radius: 10em;
              }
              .drawer-item-wrapper {
                padding: .375rem .575rem;  
                .drawer-item {
                  height: .675rem;
                  width: 55%;       
                  border-radius: 10em;
                }
              }
            }
            .drawer__footer {
              padding: .375rem .575rem;
              border-bottom-right-radius: inherit;
              .drawer-user {
                height: .575rem;
                width: 20%;
                margin-bottom: .175rem;
                border-radius: 10em;          
              }
              .drawer-username {
                height: .375rem;
                width: 30%;
                border-radius: 10em;          
              }
            }
          }
        }
      }
    }
  }
}
/* theme color */
@mixin getThemeColors($theme: null) {
  $user-preference-colors: (
    "fts-color--default" $theme-color-default $theme-color-default-dark,
    "fts-color--client" $theme-color-client $theme-color-client-dark,
    "fts-color--studio" $theme-color-studio $theme-color-studio-dark,
    "fts-color--immersive" $theme-color-immersive $theme-color-immersive-dark,
    "fts-color--violet" $theme-color-violet $theme-color-violet-dark
  );
  @each $name, $light, $dark in $user-preference-colors {
    $theme-color: $light;
    @if $theme == dark {
      $theme-color: $dark;
    }
    &.#{$name} {
      .user-preference-color-card__sidenav {
        background: map.get($theme-color, "sidenav-background");
        // .sidenav-logo {
          // background: map.get($theme-color, "sidenav");
        // }
        .sidenav-item-wrapper {
          &.sidenav-item-wrapper--active {
            background: map.get($theme-color, "sidenav-active-background");
            .sidenav-item {
              background: map.get($theme-color, "sidenav-active");;
            }
          }
          .sidenav-item {
            background: map.get($theme-color, "sidenav");
          }
        }
        .sidenav-apex {
          background: map.get($theme-color, "sidenav-apex");
        }
        .sidenav-profile {
          background: map.get($theme-color, "sidenav");
        }
      }
      .user-preference-color-card__drawer {
        background: map.get($theme-color, "drawer-background");
        .drawer-workspace {
          background: map.get($theme-color, "drawer");
        }
        .drawer-item-wrapper {
          &.drawer-item-wrapper--active {
            background: map.get($theme-color, "drawer-active-background");
            .drawer-item {
              background: map.get($theme-color, "drawer-active");
            }
          }
          .drawer-item {
            background: map.get($theme-color, "drawer");
          }
        }
        .drawer__footer {
          background: map.get($theme-color, "drawer-secondary-background");
          .drawer-user {
            background: map.get($theme-color, "drawer-secondary");
          }
          .drawer-username {
            background: map.get($theme-color, "drawer-secondary-sub");
          }
        }
      }
    }
  }
}
/* theme color:light */
.user-preference-color {
  .user-preference-color-card {
    @include getThemeColors;
  }
}
/* theme color:dark */
body.dark {
  .user-preference-color {
    .user-preference-color-card {
      @include getThemeColors(dark);
    }
  }
}
</style>

<style lang="scss" scoped>
/* app build version */
.app-build-version {
  margin-top: 1rem;
  font-size: .75rem;
  text-align: right;
  color: var(--fts-default);
}
</style>